import { useMemo, useState } from "react";
import { Download, Pencil, Plus, Trash2 } from "lucide-react";
import {
  atualizarRealizadoRegraEmissao, criarRealizadoRegraEmissao, excluirRealizadoRegraEmissao, getRealizadoRegrasEmissao,
  importarRealizadoRegrasEmissao,
} from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import { Campo } from "../../components/estoque/ui";
import {
  AvisoErro, Badge, BarraFiltros, Card, Carregando, Modal, Seletor, Vazio, botaoPequeno, estiloTabela, formatData, inputStyle,
  useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Regras de emissão por contrato (antiga aba "Regras 2" da planilha de NF): prazo de pagamento
// e observação que vão na OS do Omie, mais as alíquotas de referência. O envio da OS lê daqui
// (backend/services/realizado_regras_emissao_svc.py).

const VAZIA = {
  empresa: "", numeroContrato: "", cliente: "", cnpj: "", projeto: "", aliqPis: "", aliqCofins: "", aliqIrrf: "", aliqCsll: "",
  retencoesFolha: "", aliqIss: "", atividade: "", prazoPagamento: 0, codNbs: "", observacaoOmie: "", ativo: true,
};
const ALIQUOTAS = [
  ["aliqPis", "PIS %"], ["aliqCofins", "COFINS %"], ["aliqIrrf", "IRRF %"], ["aliqCsll", "CSLL %"], ["retencoesFolha", "Retenções folha %"], ["aliqIss", "ISS %"],
];
const fmtPct = (v) => (v === null || v === undefined || v === "" ? "—" : `${String(v).replace(".", ",")}%`);

function ModalRegra({ C, regra, empresas, onClose, onSalvo }) {
  const [form, setForm] = useState(() => ({ ...VAZIA, ...regra }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const salvar = async () => {
    setSalvando(true);
    setErro("");
    const dados = { ...form };
    ALIQUOTAS.forEach(([k]) => { dados[k] = dados[k] === "" || dados[k] === null ? null : Number(String(dados[k]).replace(",", ".")); });
    dados.prazoPagamento = Number(dados.prazoPagamento || 0);
    ["id", "origem", "atualizadoPor", "atualizadoEm"].forEach((k) => delete dados[k]);
    try {
      if (regra?.id) await atualizarRealizadoRegraEmissao(regra.id, dados);
      else await criarRealizadoRegraEmissao(dados);
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const input = { ...inputStyle(C), minHeight: 36, padding: "7px 10px", fontSize: 13 };
  return (
    <Modal
      C={C}
      largura={760}
      titulo={regra?.id ? `Editar regra · ${regra.numeroContrato}` : "Nova regra de emissão"}
      onClose={onClose}
      rodape={
        <>
          <button style={botaoPequeno(C.muted, true)} onClick={onClose}>Cancelar</button>
          <button style={botaoPequeno(C.brand)} disabled={salvando} onClick={salvar}>{salvando ? "Salvando…" : "Salvar"}</button>
        </>
      }
    >
      <AvisoErro C={C}>{erro}</AvisoErro>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        <Campo C={C} label="Empresa" obrigatorio>
          <select value={form.empresa} onChange={set("empresa")} style={input}>
            <option value="">—</option>
            {empresas.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </Campo>
        <Campo C={C} label="Nº do contrato" obrigatorio ajuda="Igual ao do Omie (barras e zeros à esquerda são ignorados)">
          <input value={form.numeroContrato || ""} onChange={set("numeroContrato")} style={input} />
        </Campo>
        <Campo C={C} label="Projeto"><input value={form.projeto || ""} onChange={set("projeto")} style={input} /></Campo>
        <Campo C={C} label="Cliente"><input value={form.cliente || ""} onChange={set("cliente")} style={input} /></Campo>
        <Campo C={C} label="CNPJ"><input value={form.cnpj || ""} onChange={set("cnpj")} style={input} /></Campo>
        <Campo C={C} label="Prazo de pagamento (dias)" obrigatorio ajuda="Vencimento da OS = data de faturamento + prazo">
          <input type="number" min={0} max={365} value={form.prazoPagamento ?? 0} onChange={set("prazoPagamento")} style={input} />
        </Campo>
        {ALIQUOTAS.map(([k, rotulo]) => (
          <Campo key={k} C={C} label={rotulo}>
            <input inputMode="decimal" value={form[k] ?? ""} onChange={set(k)} placeholder="0,00" style={input} />
          </Campo>
        ))}
        <Campo C={C} label="Atividade"><input value={form.atividade || ""} onChange={set("atividade")} style={input} /></Campo>
        <Campo C={C} label="Código NBS"><input value={form.codNbs || ""} onChange={set("codNbs")} style={input} /></Campo>
      </div>
      <div style={{ marginTop: 12 }}>
        <Campo C={C} label="Observação da OS no Omie" ajuda="Vai no campo Observações da OS">
          <textarea value={form.observacaoOmie || ""} onChange={set("observacaoOmie")} rows={3} style={{ ...input, resize: "vertical", fontFamily: "inherit" }} />
        </Campo>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13 }}>
        <input type="checkbox" checked={!!form.ativo} onChange={set("ativo")} /> Regra ativa (inativa = OS sai sem prazo e sem observação)
      </label>
    </Modal>
  );
}

export default function RegrasEmissaoRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const { dados, carregando, erro, setErro, recarregar } = useCarregar(() => getRealizadoRegrasEmissao(), []);
  const [empresa, setEmpresa] = useState("");
  const [busca, setBusca] = useState("");
  const [editando, setEditando] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultadoImportacao, setResultadoImportacao] = useState(null);
  const T = estiloTabela(C);

  const regras = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (dados?.regras || []).filter((r) => (!empresa || r.empresa === empresa)
      && (!termo || [r.numeroContrato, r.cliente, r.projeto, r.cnpj].join(" ").toLowerCase().includes(termo)));
  }, [dados, empresa, busca]);

  const importar = async () => {
    if (!window.confirm("Importar da aba \"Regras 2\" da planilha? Regras com o mesmo contrato + empresa serão ATUALIZADAS com os valores da planilha (inclusive as editadas aqui).")) return;
    setImportando(true);
    setErro("");
    try {
      setResultadoImportacao(await importarRealizadoRegrasEmissao());
      recarregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setImportando(false);
    }
  };

  const excluir = async (r) => {
    if (!window.confirm(`Excluir a regra do contrato "${r.numeroContrato}" (${r.empresa})? A OS desse contrato passa a sair sem prazo e sem observação.`)) return;
    try { await excluirRealizadoRegraEmissao(r.id); recarregar(); } catch (e) { setErro(e.message); }
  };

  return (
    <RealizadoLayout titulo="Regras de emissão" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={[{ value: "", label: "Todas" }, ...(dados?.empresas || []).map((e) => ({ value: e, label: e }))]} />
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Buscar
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Contrato, cliente, projeto ou CNPJ" style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, color: C.text, minHeight: 36, boxSizing: "border-box", width: 260 }} />
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button style={botaoPequeno(C.brand, true)} disabled={importando} onClick={importar} title="Traz as regras da aba &quot;Regras 2&quot; da planilha">
            <Download size={13} /> {importando ? "Importando…" : "Importar da planilha"}
          </button>
          <button style={botaoPequeno(C.brand)} onClick={() => setEditando({})}><Plus size={13} /> Nova regra</button>
        </div>
      </BarraFiltros>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      {resultadoImportacao && (
        <div style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>
          Importação: {resultadoImportacao.criadas} criada(s), {resultadoImportacao.atualizadas} atualizada(s), {resultadoImportacao.semAlteracao} sem alteração
          {resultadoImportacao.ignoradas.length > 0 && (
            <span style={{ color: C.danger }}> · {resultadoImportacao.ignoradas.length} ignorada(s): {resultadoImportacao.ignoradas.map((i) => `linha ${i.linha} (${i.motivo})`).join("; ")}</span>
          )}
        </div>
      )}

      {carregando && !dados ? <Carregando C={C} /> : (
        <Card C={C} titulo={`${regras.length} regra(s)`} subtitulo="Prazo de pagamento e observação são usados na OS enviada ao Omie; as alíquotas ficam como referência.">
          {regras.length === 0 ? <Vazio C={C}>Nenhuma regra. Use “Importar da planilha” para trazer a aba Regras 2.</Vazio> : (
            <div style={{ ...T.wrap, maxHeight: "68vh" }}>
              <table style={{ ...T.table, minWidth: 1250 }}>
                <thead>
                  <tr>
                    <th style={T.th} />
                    <th style={T.th}>Empresa</th>
                    <th style={T.th}>Contrato</th>
                    <th style={T.th}>Projeto / cliente</th>
                    <th style={{ ...T.th, ...T.thNum }}>Prazo (dias)</th>
                    <th style={T.th}>Observação OS</th>
                    {ALIQUOTAS.map(([k, rotulo]) => <th key={k} style={{ ...T.th, ...T.thNum }}>{rotulo.replace(" %", "")}</th>)}
                    <th style={T.th}>Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {regras.map((r) => (
                    <tr key={r.id} style={{ opacity: r.ativo ? 1 : 0.5 }}>
                      <td style={{ ...T.td, whiteSpace: "nowrap" }}>
                        <button onClick={() => setEditando(r)} title="Editar" style={{ background: "none", border: "none", color: C.brand, cursor: "pointer", padding: 2 }}><Pencil size={14} /></button>
                        <button onClick={() => excluir(r)} title="Excluir" style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 2 }}><Trash2 size={14} /></button>
                      </td>
                      <td style={T.td}>{r.empresa}</td>
                      <td style={{ ...T.td, fontWeight: 600, whiteSpace: "nowrap" }}>{r.numeroContrato}{!r.ativo && <> <Badge cor={C.muted}>inativa</Badge></>}</td>
                      <td style={{ ...T.td, maxWidth: 260 }}>
                        <div>{r.projeto || "—"}</div>
                        <div style={{ color: C.muted, fontSize: 11 }}>{r.cliente}{r.cnpj ? ` · ${r.cnpj}` : ""}</div>
                      </td>
                      <td style={{ ...T.td, ...T.tdNum, fontWeight: 700 }}>{r.prazoPagamento}</td>
                      <td style={{ ...T.td, maxWidth: 220 }}>{r.observacaoOmie || <span style={{ color: C.muted }}>—</span>}</td>
                      {ALIQUOTAS.map(([k]) => <td key={k} style={{ ...T.td, ...T.tdNum }}>{fmtPct(r[k])}</td>)}
                      <td style={{ ...T.td, whiteSpace: "nowrap", color: C.muted, fontSize: 11 }}>
                        {formatData(r.atualizadoEm)} · {r.origem === "PLANILHA" ? "planilha" : "plataforma"}<br />{r.atualizadoPor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
      {editando && (
        <ModalRegra C={C} regra={editando.id ? editando : null} empresas={dados?.empresas || []} onClose={() => setEditando(null)}
          onSalvo={() => { setEditando(null); recarregar(); }} />
      )}
    </RealizadoLayout>
  );
}
